'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppsIcon from 'public/images/sidebar-apps.svg';
import FoldIcon from 'public/images/sidebar-fold.svg';
import HomeIcon from 'public/images/sidebar-home.svg';
import SkillsIcon from 'public/images/sidebar-skills-ai.svg';
import UnfoldIcon from 'public/images/sidebar-unfold.svg';

import s from './index.module.scss';

import { Image } from '#/components/custom-image-loader';

type TopLevelShellProps = {
  children: React.ReactNode;
};

export type NavItem = {
  href: string;
  icon: typeof HomeIcon;
  label: string;
};

export const TOP_LEVEL_ROUTES = new Set(['/', '/app-center', '/agentic-hub']);
export const LIGHT_SIDEBAR_ROUTES = new Set(['/app-center', '/agentic-hub']);

function isAppCenterDetailPath(pathname: string) {
  return (
    pathname.startsWith('/app-center/detail/') || pathname.startsWith('/app-center/test/detail/')
  );
}

function isAppCenterPath(pathname: string) {
  return pathname === '/app-center' || pathname.startsWith('/app-center/');
}

export function isNavItemActive(pathname: string, href: string) {
  if (href === '/app-center') {
    return isAppCenterPath(pathname);
  }

  if (href === '/agentic-hub') {
    return pathname === '/agentic-hub' || pathname.startsWith('/agentic-hub/');
  }

  return pathname === href;
}

export const NAV_ITEMS: NavItem[] = [
  {
    href: '/',
    icon: HomeIcon,
    label: 'Home',
  },
  {
    href: '/agentic-hub',
    icon: SkillsIcon,
    label: 'AI Skills',
  },
  {
    href: '/app-center',
    icon: AppsIcon,
    label: 'Apps',
  },
];

export function normalizePathname(pathname: string | null) {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

export function TopLevelShell({ children }: TopLevelShellProps) {
  const pathname = normalizePathname(usePathname());
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldRenderShell = TOP_LEVEL_ROUTES.has(pathname) || isAppCenterDetailPath(pathname);
  const sidebarTheme = useMemo(
    () =>
      LIGHT_SIDEBAR_ROUTES.has(pathname) ||
      isAppCenterPath(pathname) ||
      isAppCenterDetailPath(pathname)
        ? 'light'
        : 'dark',
    [pathname]
  );

  if (!shouldRenderShell) {
    return <>{children}</>;
  }

  return (
    <div className={s.shell} data-expanded={isExpanded} data-theme={sidebarTheme}>
      <aside className={s.sidebar}>
        <nav className={s.nav}>
          {NAV_ITEMS.map((item) => {
            const isActive = isNavItemActive(pathname, item.href);

            return (
              <Link
                aria-current={isActive ? 'page' : undefined}
                className={s.navItem}
                data-active={isActive}
                href={item.href}
                key={item.href}
              >
                <Image
                  alt=''
                  aria-hidden={true}
                  className={[
                    s.navIcon,
                    item.href === '/' ? s.homeIcon : s.featureIcon,
                    isActive ? s.activeIcon : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  height={20}
                  src={item.icon}
                  priority
                />
                <span className={s.navLabel}>{item.label}</span>
                <span className={s.navTooltip} role='tooltip'>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </nav>
        <button
          aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          className={s.toggleButton}
          type='button'
          onClick={() => setIsExpanded((current) => !current)}
        >
          <Image
            alt=''
            aria-hidden={true}
            className={s.toggleIcon}
            height={20}
            src={isExpanded ? UnfoldIcon : FoldIcon}
            priority
          />
        </button>
      </aside>
      <div className={s.content}>{children}</div>
    </div>
  );
}
